import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../common/useAxiosPrivate";

interface AdminUser {
  id: string;
  username: string;
  roles: string[];
  name?: string | null;
  email?: string | null;
  created_at?: string;
}

const UserManagementPage: React.FC = () => {
  const axiosPrivate = useAxiosPrivate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [resetUsername, setResetUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosPrivate.get<AdminUser[]>("/admin/users");
      setUsers(response.data ?? []);
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || "Unable to load users. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const extractErrorMessage = (detail: any, fallback: string) => {
    if (!detail) {
      return fallback;
    }

    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => item?.msg || item?.message)
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join(" \n");
      }
    }

    return fallback;
  };

  const handlePasswordReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!resetUsername.trim()) {
      toast.error("Username is required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosPrivate.put(`/admin/users/${resetUsername.trim()}/password`, {
        password: newPassword,
      });
      toast.success("Password updated successfully");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = extractErrorMessage(
        detail,
        "Unable to update password. Please try again."
      );
      if (
        typeof message === "string" &&
        message.includes("Password must be at least 8 characters")
      ) {
        toast.warn(message);
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (username: string) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axiosPrivate.delete(`/admin/users/${username}`);
      toast.success("User deleted successfully");
      setUsers((prev) => prev.filter((user) => user.username !== username));
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = extractErrorMessage(
        detail,
        "Unable to delete user. Please try again."
      );
      toast.error(message);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-4 mb-4">
        <div className="flex-grow-1 w-100">
          <h2 className="mb-3">User Management</h2>
          <p className="text-muted mb-0">
            Reset passwords or remove accounts. User creation lives on the
            Create User page.
          </p>
        </div>
      </div>

      <section className="mb-5">
        <h4 className="mb-3">Update Password</h4>
        <form className="row g-3" onSubmit={handlePasswordReset}>
          <div className="col-md-4">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-control"
              value={resetUsername}
              onChange={(event) => setResetUsername(event.target.value)}
              placeholder="jane.doe"
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-control"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="col-12">
            <button className="btn blue-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0">Existing Users</h4>
          <button className="btn btn-outline-secondary" onClick={loadUsers} disabled={isLoading}>
            Refresh
          </button>
        </div>
        {isLoading ? (
          <p className="text-muted">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-muted">No users found.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Username</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id || user.username}>
                    <td>{user.username}</td>
                    <td>{user.name || "—"}</td>
                    <td>{user.email || "—"}</td>
                    <td>{user.roles?.length ? user.roles.join(", ") : "—"}</td>
                    <td>
                      {user.created_at
                        ? new Date(user.created_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteUser(user.username)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default UserManagementPage;
